import { api } from './api';

export interface Template {
  id: string;
  category: string;
  name: string;
  description: string;
  thumbnailUrl?: string | null;
  isPro?: boolean;
}

export interface TemplatesResponse {
  success: boolean;
  templates: Template[];
  categories: string[];
  hasProfessionalAccess?: boolean;
}

export interface GenerateMockupRequest {
  primaryImageId: string;
  primaryImageUrl?: string; // For brand logos and asset library
  secondaryImageId?: string;
  secondaryImageUrl?: string; // For asset library
  template: string;
  backgroundColor: string;
  productColor?: string;
  accentColor?: string;
  productTitle?: string;
  productSubtitle?: string;
  customPrompt?: string;
  showShadow?: boolean;
  showReflection?: boolean;
  glossyFinish?: boolean;
  embossEffect?: boolean;
  gradientOverlay?: boolean;
  scale?: number;
  aspectRatio?: string;
  // Brand identity fields
  brandStyle?: string;
  brandDescription?: string;
  industry?: string;
  typographyStyle?: string;
}

export interface GeneratedMockup {
  id: string;
  filename: string;
  template: string;
  url: string;
  generatedAt: string;
  storagePath?: string;
  settings?: Record<string, unknown>;
}

export interface GenerateMockupResponse {
  success: boolean;
  mockups?: GeneratedMockup[];
  error?: string;
}

export const mockupService = {
  // Get available templates
  async getTemplates(): Promise<TemplatesResponse> {
    const response = await api.get<TemplatesResponse>('/api/mockup/templates/list');
    console.log('Templates loaded:', response.data.templates.length);
    return response.data;
  },
  
  // Generate mockup
  async generateMockup(options: GenerateMockupRequest): Promise<GenerateMockupResponse> {
    try {
      console.log('Generating mockup with options:', options);
      const response = await api.post<GenerateMockupResponse>('/api/mockup/generate', options);
      console.log('Mockup generated:', response.data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        const data = error.response.data;
        if (error.response.status === 402) {
          throw new Error(data?.error?.message || data?.message || 'Insufficient credits. Please add more credits to continue generating.');
        }
        throw new Error(data?.error?.message || data?.message || 'Failed to generate mockup');
      }
      throw error;
    }
  },
  
  // Get mockup info
  async getMockupInfo(mockupId: string): Promise<GeneratedMockup> {
    const response = await api.get<{ success: boolean, mockup: GeneratedMockup }>(`/api/mockup/${mockupId}`);
    return response.data.mockup;
  },
  
  // Remove background from mockup
  async removeBackground(options: {
    mockupId: string;
    mockupUrl?: string;
    template?: string;
  }): Promise<GeneratedMockup | null> {
    try {
      console.log('Removing background from mockup:', options.mockupId);
      const response = await api.post<{ success: boolean, mockup: GeneratedMockup, error?: string }>(
        `/api/mockup/remove-background`,
        options
      );
      console.log('Background removal response:', response.data);
      
      if (response.data.success && response.data.mockup) {
        return response.data.mockup;
      }
      
      throw new Error(response.data.error || 'Failed to remove background');
    } catch (error: any) {
      console.error('Error removing background:', error);
      throw error;
    }
  }
};
